import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import cn from 'classnames'
import { CheckBoxLabel } from '@UI/CheckBox'
import Button from '@UI/New/Button'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import CreateLinkSectionTitle from '../../../../CreateLinkSectionTitle'
import CreateLinkNumberInput from '../../../../CreateLinkNumberInput'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../../../createLinkBlockInfoContent'
import infoClasses from '../../../../createLinkBlockInfo.module.css'
import {
  formatGroupPerLessonDuration,
  formatGroupPerLessonPrice,
  getGroupPerLessonDefaultsFromGroup,
  getGroupPerLessonLinkFieldDefaults,
  isSameGroupPerLessonParams,
  type GroupPerLessonLessonParams,
} from '../../../GroupPerLessonLinkFields/groupPerLessonLink.utils'
import groupClasses from '../../../GroupPerLessonLinkFields/index.module.css'
import AdditionalCourseGroupSelect, {
  type AdditionalCourseGroupOption,
} from './AdditionalCourseGroupSelect'
import AdditionalCourseDiscountLabel from './AdditionalCourseDiscountLabel'
import { useLessonAdditionalCourseForm } from './useLessonAdditionalCourseForm'
import { ICourseEnrollmentItem } from './types'
import spclasses from '../../../../../SetCourseClassDirectionModal/purchase.module.css'

type GroupPerLessonAdditionalCourseFieldsProps = {
  order: ICourseEnrollmentItem
  tariff?: ICourseTariff | null
  defaultDiscount?: number | null
  onChange: (
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => void
}

const NO_GROUP_TOOLTIP =
  'Группа не выбрана. Включите чекбокс «Распределить ученика в группу» и выберите группу в селекторе.'

const TARIFF_LOCKED_BY_GROUP_TOOLTIP =
  'При выбранной группе длительность берётся из группы. Можно изменить только стоимость занятия.'

const DURATION_LOCKED_BY_GROUP_TOOLTIP =
  'Длительность задаётся выбранной группой и не редактируется'

const ALREADY_MATCHES_TARIFF_TOOLTIP =
  'В полях уже указаны те же длительность и стоимость, что в тарифе'

const ALREADY_MATCHES_GROUP_PRICE_TOOLTIP =
  'Стоимость уже совпадает со значением из группы'

const SOURCES_TABLE_INFO = (
  <div className={infoClasses.root}>
    <div className={infoClasses.description}>
      Здесь показаны длительность и цена из тарифа и из выбранной группы.
      Кнопка «Заполнить» копирует их в поля выше.
    </div>
    <div className={infoClasses.description} style={{ marginTop: 6 }}>
      Без группы можно менять оба поля или взять значения из тарифа.
    </div>
    <div className={infoClasses.description} style={{ marginTop: 6 }}>
      С группой длительность берётся из группы и не меняется — править можно
      только цену.
    </div>
  </div>
)

type FillButtonProps = {
  disabled: boolean
  disabledReason?: string
  onClick: () => void
}

const FillButton: FC<FillButtonProps> = ({
  disabled,
  disabledReason,
  onClick,
}) => {
  const button = (
    <Button
      type="button"
      color="gray"
      className={groupClasses.fillButton}
      disabled={disabled}
      onClick={onClick}
    >
      Заполнить
    </Button>
  )

  if (!disabled || !disabledReason) {
    return button
  }

  return (
    <TooltipWrapper title={disabledReason}>
      <span className={groupClasses.fillButtonWrap}>{button}</span>
    </TooltipWrapper>
  )
}

/**
 * Доп. курс group_per_lesson: поля + таблица источников (как у основного).
 */
const GroupPerLessonAdditionalCourseFields: FC<
  GroupPerLessonAdditionalCourseFieldsProps
> = ({ order, tariff, defaultDiscount = null, onChange }) => {
  const lessonForm = useLessonAdditionalCourseForm({
    order,
    selectedTariff: tariff,
    defaultDiscount,
    onChange,
  })

  const [selectedGroupMeta, setSelectedGroupMeta] =
    useState<AdditionalCourseGroupOption | null>(null)

  const onSelectedMetaChange = useCallback(
    (meta: AdditionalCourseGroupOption | null) => {
      setSelectedGroupMeta(meta)
    },
    [],
  )

  const hasSelectedGroup = order.group_id != null && selectedGroupMeta != null

  const currentParams = useMemo<GroupPerLessonLessonParams>(
    () => ({
      lesson_duration:
        order.lesson_duration != null &&
        Number.isFinite(Number(order.lesson_duration))
          ? Number(order.lesson_duration)
          : null,
      first_price:
        order.first_price != null && Number.isFinite(Number(order.first_price))
          ? Number(order.first_price)
          : null,
    }),
    [order.first_price, order.lesson_duration],
  )

  const tariffParams = useMemo(
    () => getGroupPerLessonLinkFieldDefaults(tariff),
    [tariff],
  )

  const groupParams = useMemo(() => {
    if (!selectedGroupMeta) return null
    return getGroupPerLessonDefaultsFromGroup(tariff, {
      lessonDuration: selectedGroupMeta.lessonDuration,
      studentLessonPrice: selectedGroupMeta.studentLessonPrice,
    })
  }, [selectedGroupMeta, tariff])

  const groupDisplayParams = useMemo<GroupPerLessonLessonParams | null>(() => {
    if (!selectedGroupMeta) return null
    const duration = Number(selectedGroupMeta.lessonDuration)
    const price = Number(selectedGroupMeta.studentLessonPrice)
    return {
      lesson_duration:
        Number.isFinite(duration) && duration >= 1
          ? Math.trunc(duration)
          : null,
      first_price: Number.isFinite(price) ? price : null,
    }
  }, [selectedGroupMeta])

  const matchesTariff = isSameGroupPerLessonParams(currentParams, tariffParams)
  const matchesGroupPrice =
    groupParams != null &&
    Number.isFinite(Number(currentParams.first_price)) &&
    Number.isFinite(Number(groupParams.first_price)) &&
    Number(currentParams.first_price) === Number(groupParams.first_price)

  const tariffRowDisabled = hasSelectedGroup
  const tariffFillDisabled = hasSelectedGroup || matchesTariff
  const tariffFillReason = hasSelectedGroup
    ? TARIFF_LOCKED_BY_GROUP_TOOLTIP
    : matchesTariff
    ? ALREADY_MATCHES_TARIFF_TOOLTIP
    : undefined

  const groupFillDisabled = !hasSelectedGroup || matchesGroupPrice
  const groupFillReason = hasSelectedGroup
    ? matchesGroupPrice
      ? ALREADY_MATCHES_GROUP_PRICE_TOOLTIP
      : undefined
    : undefined

  const renderValues = (
    params: GroupPerLessonLessonParams | null,
  ): ReactNode => {
    if (!params) {
      return (
        <>
          <span className={groupClasses.sourceMuted}>—</span>
          <span className={groupClasses.sourceMuted}>—</span>
        </>
      )
    }

    return (
      <>
        <span>{formatGroupPerLessonDuration(params.lesson_duration)}</span>
        <span>{formatGroupPerLessonPrice(params.first_price)}</span>
      </>
    )
  }

  const tariffRow = (
    <div
      className={cn(
        groupClasses.sourcesRow,
        tariffRowDisabled && groupClasses.sourcesRowDisabled,
      )}
    >
      <span className={groupClasses.sourceName}>Тариф курса</span>
      {renderValues(tariffParams)}
      <FillButton
        disabled={tariffFillDisabled}
        disabledReason={tariffRowDisabled ? undefined : tariffFillReason}
        onClick={lessonForm.applyGroupPerLessonFromTariff}
      />
    </div>
  )

  const groupRow = (
    <div
      className={cn(
        groupClasses.sourcesRow,
        !hasSelectedGroup && groupClasses.sourcesRowDisabled,
      )}
    >
      <span className={groupClasses.sourceName}>Группа</span>
      {renderValues(hasSelectedGroup ? groupDisplayParams : null)}
      <FillButton
        disabled={groupFillDisabled}
        disabledReason={groupFillReason}
        onClick={() =>
          lessonForm.applyGroupPerLessonPriceFromGroup({
            lessonDuration: selectedGroupMeta?.lessonDuration,
            studentLessonPrice: selectedGroupMeta?.studentLessonPrice,
          })
        }
      />
    </div>
  )

  return (
    <>
      <CreateLinkSectionTitle
        title="Параметры занятия"
        infoText={withCreateLinkBlockTariffTypesHint(
          'Параметры занятия для группового тарифа с оплатой по занятию.',
          CREATE_LINK_BLOCK_TARIFF_TYPES.groupPerLesson,
        )}
      />

      {hasSelectedGroup ? (
        <div className={groupClasses.modeHint}>
          Группа выбрана: длительность берётся из группы, изменить можно только
          стоимость
        </div>
      ) : null}

      <div className={spclasses.inputWithLabel}>
        {hasSelectedGroup ? (
          <TooltipWrapper title={DURATION_LOCKED_BY_GROUP_TOOLTIP}>
            <div className={groupClasses.disabledFieldWrap}>
              <CreateLinkNumberInput
                className={cn(spclasses.input, spclasses.disabledInput)}
                title="Длительность занятия (мин.)"
                integer
                min={1}
                max={1440}
                placeholder={60}
                value={order.lesson_duration}
                disabled
              />
            </div>
          </TooltipWrapper>
        ) : (
          <CreateLinkNumberInput
            className={spclasses.input}
            title="Длительность занятия (мин.)"
            integer
            min={1}
            max={1440}
            placeholder={60}
            value={order.lesson_duration}
            onChange={lessonForm.onChangeDuration}
          />
        )}
      </div>

      <div className={spclasses.inputWithLabel}>
        <CreateLinkNumberInput
          className={spclasses.input}
          title="Стоимость одного занятия (₽)"
          min={0}
          placeholder={0}
          value={order.first_price}
          onChange={lessonForm.onChangeFirstPrice}
        />
      </div>

      <div className={groupClasses.groupSelectSlot}>
        <AdditionalCourseGroupSelect
          courseId={order.courseId}
          tariffType={tariff?.type}
          value={order.group_id}
          onSelectedMetaChange={onSelectedMetaChange}
          onChange={(groupId, meta) => {
            setSelectedGroupMeta(meta ?? null)
            lessonForm.onChangeGroup(groupId, meta)
          }}
        />
      </div>

      <CreateLinkSectionTitle
        title="Источники длительности и стоимости"
        infoText={SOURCES_TABLE_INFO}
        className={groupClasses.sourcesTitle}
      />
      <div className={groupClasses.sourcesTable}>
        <div className={groupClasses.sourcesHead}>
          <span>Источник</span>
          <span>Длительность</span>
          <span>Стоимость</span>
          <span />
        </div>
        {tariffRowDisabled ? (
          <TooltipWrapper title={TARIFF_LOCKED_BY_GROUP_TOOLTIP}>
            <div className={groupClasses.tooltipRow}>{tariffRow}</div>
          </TooltipWrapper>
        ) : (
          tariffRow
        )}
        {hasSelectedGroup ? (
          groupRow
        ) : (
          <TooltipWrapper title={NO_GROUP_TOOLTIP}>
            <div className={groupClasses.tooltipRow}>{groupRow}</div>
          </TooltipWrapper>
        )}
      </div>

      <div className={spclasses.inputWithLabel}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CheckBoxLabel
            type="button"
            value={lessonForm.isValueFixed}
            onClick={lessonForm.onToggleValueFixed}
            label="Зафиксировать кол-во занятий для оплаты"
            className={spclasses.checkboxWithLabel}
            style={{
              justifyContent: 'flex-start',
              paddingTop: '4px',
              paddingBottom: '4px',
              height: 'auto',
            }}
          />
          <InfoComp
            text={withCreateLinkBlockTariffTypesHint(
              'По умолчанию клиент при покупке сам выбирает количество занятий. При включении чекбокса количество фиксируется.',
              CREATE_LINK_BLOCK_TARIFF_TYPES.lessonCount,
            )}
          />
        </div>
        <div style={{ paddingLeft: '8px', paddingTop: '4px' }}>
          <SliderField
            label="Количество занятий"
            value={lessonForm.lessonCount}
            extraContent={lessonForm.lessonCount.toString()}
            min={1}
            max={100}
            onChange={lessonForm.onChangeLessonCount}
          />
        </div>
      </div>

      <div className={spclasses.inputWithLabel}>
        <AdditionalCourseDiscountLabel />
        {lessonForm.isDiscountEditable ? (
          <CreateLinkNumberInput
            className={spclasses.input}
            integer
            min={0}
            placeholder="0"
            value={lessonForm.discountValue || null}
            onChange={lessonForm.onChangeDiscount}
          />
        ) : (
          <TooltipWrapper
            placement="top"
            title="Итоговую сумму можно менять вручную — скидка в этом режиме недоступна. Снимите фиксацию количества занятий, чтобы задать скидку."
          >
            <span style={{ display: 'block', cursor: 'not-allowed' }}>
              <CreateLinkNumberInput
                className={cn(spclasses.input, spclasses.disabledInput)}
                integer
                min={0}
                placeholder="0"
                value={lessonForm.discountValue || null}
                disabled
              />
            </span>
          </TooltipWrapper>
        )}
      </div>

      <div className={spclasses.inputWithLabel}>
        {lessonForm.isPriceEditable ? (
          <CreateLinkNumberInput
            className={spclasses.input}
            title="Итого к оплате (₽)"
            min={0}
            value={order.price ?? lessonForm.suggestedPrice}
            onChange={lessonForm.onChangePrice}
          />
        ) : (
          <TooltipWrapper
            placement="top"
            title="Итого считается автоматически (цена занятия × количество). Чтобы задать сумму вручную, зафиксируйте количество занятий."
          >
            <span style={{ display: 'block', cursor: 'not-allowed' }}>
              <CreateLinkNumberInput
                className={cn(spclasses.input, spclasses.disabledInput)}
                title="Итого к оплате (₽)"
                min={0}
                value={lessonForm.suggestedPrice}
                disabled
              />
            </span>
          </TooltipWrapper>
        )}
        <div className={spclasses.tariffTypeHint} style={{ marginTop: 4 }}>
          Расчётная сумма: {lessonForm.suggestedPrice} ₽ (
          {order.first_price ?? 0} ₽ × {lessonForm.lessonCount}
          {lessonForm.discountValue > 0
            ? ` − скидка ${lessonForm.discountValue} ₽`
            : ''}
          )
        </div>
      </div>
    </>
  )
}

export default GroupPerLessonAdditionalCourseFields
